import { useEffect, useRef, useState } from 'react';
import { api } from '../../lib/api';

/**
 * Vessel Name — a real search + dropdown, not a plain text box.
 *
 * Logistics only ever gets a vessel NAME off the carrier's booking
 * confirmation, but AIS tracking subscribes by MMSI — so without this hop the
 * live tracking board (Vessel Tracker) stays empty no matter how carefully
 * the form is filled. Typing resolves against the vessel registry
 * (`/vessel-tracking/lookup` — Wikidata seed + AIS harvest + manual entries)
 * and the matches drop down directly under the field, the way a normal
 * autocomplete does — this used to be a separate helper PANEL below the
 * input, which read as "some extra tool", not "the field's own dropdown".
 *
 * It never auto-picks. Vessel names are genuinely ambiguous (ships get
 * renamed and names get reused, which is the whole reason IMO numbers exist),
 * so candidates are listed and the operator confirms. "Other" is always on
 * the list too — typing manually is the field's normal behaviour, not a
 * fallback bolted on the side, and there is always a way to enter the
 * IMO/MMSI by hand when the registry has never seen this ship. Once
 * saved, it is what starts the AIS feed for this order on the Vessel Tracker.
 */

export type VesselCandidate = {
  name: string;
  imo: string;
  mmsi: string;
  callSign: string;
  vesselType: string;
  flag: string;
  source: 'wikidata' | 'ais' | 'manual';
  lastSeenAt: string | null;
  matchedOn: string;
  trackable: boolean;
};

const SOURCE_LABEL: Record<VesselCandidate['source'], string> = {
  wikidata: 'Registry',
  ais: 'Seen on AIS',
  manual: 'Entered by us',
};

export default function VesselResolver({
  value, onChangeText, imo, mmsi, onPick, placeholder,
}: {
  /** The vessel name text itself — this component now owns that field. */
  value: string;
  onChangeText: (v: string) => void;
  imo: string;
  mmsi: string;
  onPick: (v: { imo: string; mmsi: string; name?: string }) => void;
  placeholder?: string;
}) {
  const [candidates, setCandidates] = useState<VesselCandidate[]>([]);
  const [busy, setBusy] = useState(false);
  const [searched, setSearched] = useState('');
  const [open, setOpen] = useState(false);
  const [manual, setManual] = useState(false);
  const [mImo, setMImo] = useState('');
  const [mMmsi, setMMmsi] = useState('');
  const [err, setErr] = useState('');
  const boxRef = useRef<HTMLDivElement>(null);

  // Debounced so a lookup fires per pause, not per keystroke.
  useEffect(() => {
    const q = value.trim();
    if (q.length < 3) { setCandidates([]); setSearched(''); return; }
    const t = setTimeout(async () => {
      setBusy(true);
      try {
        const r = await api.get<{ candidates: VesselCandidate[]; normalised: string }>(
          `/api/procurement/vessel-tracking/lookup?q=${encodeURIComponent(q)}`);
        setCandidates(r.candidates || []);
        setSearched(r.normalised || q);
      } catch { setCandidates([]); }
      finally { setBusy(false); }
    }, 400);
    return () => clearTimeout(t);
  }, [value]);

  // Click outside the whole combobox (input + dropdown) closes it — standard
  // autocomplete behaviour, so the dropdown doesn't sit open over the rest of
  // the form once the operator has moved on.
  useEffect(() => {
    if (!open) return;
    const onDocClick = (e: MouseEvent) => {
      if (boxRef.current && !boxRef.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener('mousedown', onDocClick);
    return () => document.removeEventListener('mousedown', onDocClick);
  }, [open]);

  async function saveManual() {
    setErr('');
    try {
      const r = await api.post<{ ok: boolean; message?: string; trackable: boolean }>(
        '/api/procurement/vessel-tracking/registry',
        { name: value.trim(), imo: mImo.trim(), mmsi: mMmsi.trim() });
      if (!r.ok) { setErr(r.message || 'Could not save'); return; }
      onPick({ imo: mImo.trim(), mmsi: mMmsi.trim() });
      setManual(false);
      setOpen(false);
    } catch (e: any) {
      setErr(e?.message || 'Could not save');
    }
  }

  const resolved = !!mmsi || !!imo;
  const showDropdown = open && (candidates.length > 0 || (!busy && searched));

  return (
    <div ref={boxRef} className="relative">
      <div className="flex items-center gap-1.5">
        <input
          className="input flex-1 font-mono"
          value={value}
          placeholder={placeholder || 'e.g. MSC Isabella'}
          onChange={(e) => { onChangeText(e.target.value); setOpen(true); setManual(false); }}
          onFocus={() => setOpen(true)}
        />
        {resolved && (
          <span
            className="shrink-0 rounded-full border border-emerald-200 bg-emerald-50 px-2 py-0.5 text-[10px] font-semibold text-emerald-800"
            title={`IMO ${imo || '—'} · MMSI ${mmsi || '—'}`}
          >
            {mmsi ? '✓ Trackable' : 'IMO only'}
          </span>
        )}
      </div>

      {showDropdown && (
        <div className="absolute z-20 mt-1 w-full rounded-lg border border-slate-200 bg-white p-1.5 shadow-lg">
          {busy && <div className="px-2 py-1.5 text-[11px] text-slate-400">searching…</div>}

          {!busy && candidates.length > 0 && (
            <ul className="max-h-48 space-y-1 overflow-y-auto">
              {candidates.map((c, i) => {
                const picked = (!!c.imo && c.imo === imo) || (!!c.mmsi && c.mmsi === mmsi);
                return (
                  <li key={`${c.imo || c.mmsi || c.name}-${i}`}>
                    <button
                      type="button"
                      onClick={() => { onPick({ imo: c.imo, mmsi: c.mmsi, name: c.name }); setOpen(false); }}
                      className={
                        'flex w-full flex-wrap items-center gap-x-2 gap-y-0.5 rounded border px-2 py-1.5 text-left transition ' +
                        (picked ? 'border-emerald-300 bg-emerald-50' : 'border-slate-200 bg-white hover:border-blue-300 hover:bg-blue-50')
                      }
                    >
                      <span className="font-semibold text-slate-800">{c.name}</span>
                      <span className="font-mono text-[10.5px] text-slate-500">
                        IMO {c.imo || '—'} · MMSI {c.mmsi || '—'}
                      </span>
                      {!c.trackable && (
                        <span className="rounded bg-amber-100 px-1 py-0.5 text-[9.5px] font-semibold text-amber-800">
                          no MMSI
                        </span>
                      )}
                      <span className="ml-auto text-[9.5px] uppercase tracking-wide text-slate-400">
                        {SOURCE_LABEL[c.source]} · {c.matchedOn}
                      </span>
                    </button>
                  </li>
                );
              })}
            </ul>
          )}

          {!busy && !candidates.length && searched && (
            <div className="px-2 py-1.5 text-[11px] text-slate-500">
              No match in the registry — feeders and coastal vessels are often missing.
            </div>
          )}

          {/* "Other" — always available, whether or not the registry found
              anything. This is a normal item in the dropdown, not a
              side-panel the operator has to notice separately. */}
          <button
            type="button"
            onClick={() => { setManual(true); setMImo(imo); setMMmsi(mmsi); }}
            className="mt-1 flex w-full items-center gap-2 rounded border border-dashed border-slate-300 bg-slate-50 px-2 py-1.5 text-left text-[11.5px] font-semibold text-blue-700 hover:bg-blue-50"
          >
            <span aria-hidden>+</span> Not on the list — enter IMO / MMSI manually
          </button>

          {manual && (
            <div className="mt-1.5 space-y-2 rounded border border-slate-200 bg-white p-2">
              <p className="text-[10.5px] text-slate-500">
                Both are on the carrier's booking confirmation. MMSI (9 digits) is what drives live
                tracking on the Vessel Tracker; IMO (7 digits) is the permanent hull id. Saved to the
                registry so this vessel resolves on its own next time.
              </p>
              <div className="flex flex-wrap gap-2">
                <input className="input flex-1 font-mono" placeholder="IMO (7 digits)" value={mImo}
                  onChange={(e) => setMImo(e.target.value.replace(/\D/g, '').slice(0, 7))} />
                <input className="input flex-1 font-mono" placeholder="MMSI (9 digits)" value={mMmsi}
                  onChange={(e) => setMMmsi(e.target.value.replace(/\D/g, '').slice(0, 9))} />
                <button type="button" className="pc-btn pc-btn-primary" onClick={saveManual}>Save</button>
              </div>
              {err && <div className="text-[11px] font-semibold text-rose-700">{err}</div>}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
